/** @format */

import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useUsers } from "../hooks/useUsers";
import { StudentUser } from "../types";
import {
  UserIcon,
  EnvelopeIcon,
  PhoneIcon,
  AcademicCapIcon,
  CalendarIcon,
  ShieldCheckIcon,
  ArrowLeftIcon,
  UserGroupIcon,
  BookOpenIcon,
  CurrencyDollarIcon,
} from "@heroicons/react/24/outline";
import LoadingSpinner from "../components/LoadingSpinner";
import { db } from "../firebase/config";
import { doc, getDoc, setDoc, addDoc, collection, serverTimestamp, setDoc as fbSetDoc, updateDoc, increment } from "firebase/firestore";

const PublicProfile: React.FC = () => {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { fetchUser, getUserDisplayName, getUserAvatar } = useUsers();
  
  const [user, setUser] = useState<StudentUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dmMessage, setDmMessage] = useState("");
  const [isSending, setIsSending] = useState(false);
  const [sendSuccess, setSendSuccess] = useState<string | null>(null);
  const [sendError, setSendError] = useState<string | null>(null);

  useEffect(() => {
    const loadUserProfile = async () => {
      if (!userId) {
        setError("User ID is required");
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        
        const userData = await fetchUser(userId);
        if (userData) {
          setUser(userData);
        } else {
          setError("User not found");
        }
      } catch (err) {
        console.error("Error loading user profile:", err);
        setError("Failed to load user profile");
      } finally {
        setLoading(false);
      }
    };

    loadUserProfile();
  }, [userId, fetchUser]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  if (error || !user) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pt-8">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 text-center">
            <div className="text-red-500 mb-4">
              <ShieldCheckIcon className="h-16 w-16 mx-auto" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-4">
              {error || "User Not Found"}
            </h1>
            <p className="text-gray-600 mb-6">
              The user profile you're looking for doesn't exist or has been removed.
            </p>
            <button
              onClick={() => navigate(-1)}
              className="inline-flex items-center px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white font-medium rounded-lg transition-colors"
            >
              <ArrowLeftIcon className="h-4 w-4 mr-2" />
              Go Back
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Don't show private information if not the current user
  const isOwnProfile = currentUser?.uid === userId;
  const displayName = user.name || user.email?.split('@')[0] || 'Unknown User';
  const avatar = user.avatar;

  const ensureDmThreadAndSend = async () => {
    if (!currentUser) {
      navigate(`/signin?next=/profile/${userId}`);
      return;
    }
    if (!userId || !dmMessage.trim()) return;

    try {
      setIsSending(true);
      setSendError(null);
      setSendSuccess(null);
      // Deterministic thread id for pair
      const [a, b] = [currentUser.uid, userId].sort();
      const threadId = `${a}_${b}`;

      const threadRef = doc(db, "dm_threads", threadId);
      const existing = await getDoc(threadRef);

      const nowTs = serverTimestamp();
      const currentUserName = currentUser.name || currentUser.email || "You";
      const otherUserName = displayName;

      if (!existing.exists()) {
        await setDoc(threadRef, {
          id: threadId,
          participants: [a, b],
          createdAt: nowTs,
          updatedAt: nowTs,
          lastMessagePreview: dmMessage.slice(0, 120),
          lastMessageFrom: currentUser.uid,
          lastMessageAt: nowTs,
          participantNames: {
            [currentUser.uid]: currentUserName,
            [userId]: otherUserName,
          },
          unreadBy: { [userId]: 1, [currentUser.uid]: 0 },
        });
      } else {
        await setDoc(threadRef, {
          updatedAt: nowTs,
          lastMessagePreview: dmMessage.slice(0, 120),
          lastMessageFrom: currentUser.uid,
          lastMessageAt: nowTs,
        }, { merge: true });
        // Increment receiver's unread
        await updateDoc(threadRef, {
          [`unreadBy.${userId}`]: increment(1),
          [`unreadBy.${currentUser.uid}`]: 0
        } as any);
      }

      // Add message in subcollection
      await addDoc(collection(db, "dm_threads", threadId, "messages"), {
        senderId: currentUser.uid,
        receiverId: userId,
        content: dmMessage.trim(),
        createdAt: nowTs,
        readBy: [currentUser.uid],
      });

      setDmMessage("");
      setSendSuccess("Message sent");
      setTimeout(() => setSendSuccess(null), 2000);
    } catch (err) {
      console.error("Failed to send DM:", err);
      setSendError("Failed to send message. Please try again.");
    } finally {
      setIsSending(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pt-8">
        {/* Header */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 mb-8">
          <div className="flex items-center space-x-4">
            {/* Back Button */}
            <button
              onClick={() => navigate(-1)}
              className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
            >
              <ArrowLeftIcon className="h-6 w-6" />
            </button>

            {/* Profile Image */}
            <div className="relative">
              <div className="w-20 h-20 bg-gradient-to-br from-primary-100 to-primary-200 rounded-full flex items-center justify-center overflow-hidden border border-primary-200 shadow-sm">
                {avatar ? (
                  <img 
                    src={avatar} 
                    alt={displayName} 
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <span className="text-3xl font-bold text-primary-600">
                    {displayName.charAt(0).toUpperCase()}
                  </span>
                )}
              </div>
            </div>
            
            {/* User Info */}
            <div className="flex-1">
              <h1 className="text-2xl font-bold text-gray-900">{displayName}</h1>
              <div className="flex items-center space-x-4 mt-2">
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-primary-100 text-primary-800">
                  <UserGroupIcon className="h-4 w-4 mr-1" />
                  {user.role === 'pending' ? 'Pending Author' : user.role}
                </span>
                <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                  user.status === 'Online' 
                    ? 'bg-green-100 text-green-800' 
                    : user.status === 'pending'
                    ? 'bg-yellow-100 text-yellow-800'
                    : 'bg-gray-100 text-gray-800'
                }`}>
                  <ShieldCheckIcon className="h-4 w-4 mr-1" />
                  {user.status}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Profile Details */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Basic Information */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <UserIcon className="h-5 w-5 mr-2 text-primary-600" />
              Basic Information
            </h2>
            <div className="space-y-4">
              {user.program && (
                <div className="flex items-center">
                  <AcademicCapIcon className="h-5 w-5 mr-3 text-gray-400" />
                  <div>
                    <p className="text-sm font-medium text-gray-600">Program</p>
                    <p className="text-gray-900">{user.program}</p>
                  </div>
                </div>
              )}
              
              {isOwnProfile && user.telephone && (
                <div className="flex items-center">
                  <PhoneIcon className="h-5 w-5 mr-3 text-gray-400" />
                  <div>
                    <p className="text-sm font-medium text-gray-600">Phone</p>
                    <p className="text-gray-900">{user.telephone}</p>
                  </div>
                </div>
              )}

              {isOwnProfile && user.registern && (
                <div className="flex items-center">
                  <BookOpenIcon className="h-5 w-5 mr-3 text-gray-400" />
                  <div>
                    <p className="text-sm font-medium text-gray-600">Registration Number</p>
                    <p className="text-gray-900">{user.registern}</p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Account Information */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <ShieldCheckIcon className="h-5 w-5 mr-2 text-primary-600" />
              Account Information
            </h2>
            <div className="space-y-4">
              <div className="flex items-center">
                <CalendarIcon className="h-5 w-5 mr-3 text-gray-400" />
                <div>
                  <p className="text-sm font-medium text-gray-600">Member Since</p>
                  <p className="text-gray-900">
                    {user.createdAt ? (() => {
                      try {
                        const year = new Date(user.createdAt).getFullYear();
                        return isNaN(year) ? "N/A" : year;
                      } catch (error) {
                        return "N/A";
                      }
                    })() : "N/A"}
                  </p>
                </div>
              </div>

              {isOwnProfile && (
                <>
                  <div className="flex items-center">
                    <CurrencyDollarIcon className="h-5 w-5 mr-3 text-gray-400" />
                    <div>
                      <p className="text-sm font-medium text-gray-600">Total Referrals</p>
                      <p className="text-gray-900">{user.totalrefs || 0}</p>
                    </div>
                  </div>

                  {user.earnings > 0 && (
                    <div className="flex items-center">
                      <CurrencyDollarIcon className="h-5 w-5 mr-3 text-gray-400" />
                      <div>
                        <p className="text-sm font-medium text-gray-600">Earnings</p>
                        <p className="text-gray-900">GH₵ {user.earnings.toFixed(2)}</p>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>

        {/* Contact Information (only for own profile) */}
        {isOwnProfile && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mt-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <EnvelopeIcon className="h-5 w-5 mr-2 text-primary-600" />
              Contact Information
            </h2>
            <div className="space-y-4">
              <div className="flex items-center">
                <EnvelopeIcon className="h-5 w-5 mr-3 text-gray-400" />
                <div>
                  <p className="text-sm font-medium text-gray-600">Email</p>
                  <p className="text-gray-900">{user.email}</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Private Message Box (only when viewing someone else's profile) */}
        {!isOwnProfile && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mt-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
              <EnvelopeIcon className="h-5 w-5 mr-2 text-primary-600" />
              Send a private message
            </h2>
            {!currentUser ? (
              <div className="text-sm text-gray-700">
                Please <button onClick={() => navigate(`/signin?next=/profile/${userId}`)} className="text-primary-600 hover:underline">sign in</button> to send a message.
              </div>
            ) : (
              <>
                <textarea
                  value={dmMessage}
                  onChange={(e) => setDmMessage(e.target.value)}
                  placeholder={`Message ${displayName}...`}
                  className="w-full border border-gray-300 rounded-lg p-3 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm min-h-[96px]"
                  maxLength={1000}
                />
                <div className="mt-3 flex items-center justify-between">
                  <div className="text-xs text-gray-500">{dmMessage.length}/1000</div>
                  <div className="flex items-center space-x-3">
                    {sendError && <span className="text-xs text-red-600">{sendError}</span>}
                    {sendSuccess && <span className="text-xs text-green-600">{sendSuccess}</span>}
                    <button
                      onClick={ensureDmThreadAndSend}
                      disabled={isSending || dmMessage.trim().length === 0}
                      className={`px-5 py-2 rounded-lg text-white text-sm font-medium ${isSending || dmMessage.trim().length === 0 ? 'bg-gray-400 cursor-not-allowed' : 'bg-primary-600 hover:bg-primary-700'} transition-colors`}
                    >
                      {isSending ? 'Sending...' : 'Send Message'}
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        )}

        {/* Action Buttons */}
        <div className="mt-8 flex justify-center space-x-4">
          <button
            onClick={() => navigate(-1)}
            className="px-6 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-lg transition-colors"
          >
            Go Back
          </button>
          
          {isOwnProfile && (
            <button
              onClick={() => navigate('/profile')}
              className="px-6 py-3 bg-primary-600 hover:bg-primary-700 text-white font-medium rounded-lg transition-colors"
            >
              Edit Profile
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default PublicProfile;
